import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { ChevronsUpDown, Check, Search, Clock, X } from 'lucide-react';

const fmtDuree = (h) => (h ? `${h} h${h >= 7 ? ` · ${Math.round((h / 7) * 10) / 10} j` : ''}` : '—');

// Liste déroulante des formations : 3 colonnes (code / intitulé / durée), recherche texte et filtre par durée
export const FormationPicker = ({ formations = [], value, onChange, placeholder = 'Sélectionner une formation', disabled, testId = 'select-formation', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [duree, setDuree] = useState('all');
  const selected = formations.find(f => f.id === value);
  const durees = useMemo(() => [...new Set(formations.map(f => f.duree_totale_heures || 0).filter(h => h > 0))].sort((a, b) => a - b), [formations]);
  const liste = useMemo(() => {
    const s = q.trim().toLowerCase();
    return [...formations]
      .filter(f => (duree === 'all' || (f.duree_totale_heures || 0) === Number(duree)) && (!s || `${f.code} ${f.titre}`.toLowerCase().includes(s)))
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', 'fr'));
  }, [formations, q, duree]);
  const pick = (f) => { onChange(f.id, f); setOpen(false); setQ(''); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testId}
          className={`flex h-10 w-full min-w-0 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}>
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-xs text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded shrink-0">{selected.code}</span>
              <span className="truncate">{selected.titre}</span>
              <span className="text-xs text-slate-500 shrink-0 hidden sm:inline">· {fmtDuree(selected.duree_totale_heures)}</span>
            </span>
          ) : <span className="text-muted-foreground truncate">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[520px] max-w-[95vw] p-0" data-testid="formation-picker-content">
        <div className="flex items-center gap-2 p-2 border-b bg-slate-50">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code ou intitulé…" className="h-8 pl-8 text-sm bg-white" data-testid="formation-picker-search" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 shrink-0" data-testid="formation-picker-durees">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <select value={duree} onChange={(e) => setDuree(e.target.value)} data-testid="formation-picker-duree-select"
              className={`h-8 rounded-md border bg-white px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 ${duree !== 'all' ? 'border-indigo-500 text-indigo-700' : 'border-slate-200 text-slate-700'}`}>
              <option value="all">Toutes durées</option>
              {durees.map(h => <option key={h} value={String(h)}>{h} h{h >= 7 ? ` (${Math.round((h / 7) * 10) / 10} j)` : ''}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-[110px_1fr_90px] gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 border-b">
          <span>Code</span><span>Intitulé</span><span className="text-right">Durée</span>
        </div>
        <div className="max-h-[320px] overflow-y-auto" data-testid="formation-picker-list">
          {liste.length === 0 && <p className="px-3 py-6 text-sm text-slate-500 text-center">Aucune formation ne correspond</p>}
          {liste.map(f => (
            <button key={f.id} type="button" onClick={() => pick(f)} data-testid={`formation-option-${f.id}`}
              className={`w-full grid grid-cols-[110px_1fr_90px] gap-2 items-center px-3 py-2 text-left text-sm hover:bg-indigo-50 border-b border-slate-50 ${f.id === value ? 'bg-indigo-50/70' : ''}`}>
              <span className="font-mono text-xs text-indigo-700 truncate">{f.code}</span>
              <span className="truncate text-slate-800 flex items-center gap-1 min-w-0">{f.id === value && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}<span className="truncate">{f.titre}</span></span>
              <span className="text-right text-xs text-slate-600 tabular-nums">{fmtDuree(f.duree_totale_heures)}</span>
            </button>
          ))}
        </div>
        {(q || duree !== 'all') && (
          <div className="px-3 py-1.5 border-t bg-slate-50 flex items-center justify-between text-[11px] text-slate-500">
            <span>{liste.length} / {formations.length} formation(s)</span>
            <button type="button" onClick={() => { setQ(''); setDuree('all'); }} className="inline-flex items-center gap-1 hover:text-slate-800" data-testid="formation-picker-reset"><X className="w-3 h-3" /> Effacer les filtres</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
